import { parseServerTime } from "@/filters/DateAndTime";
import GraphRanges from "@/mixins/GraphRanges";
import { navigationNamespace } from "@/store/modules/navigation";
import { patientNamespace } from "@/store/modules/patient";
import { Patient } from "@/store/modules/patient/types";
import _ from "lodash";
import { mixins } from "vue-class-component";
import { Component, Prop } from "vue-property-decorator";
import { Getter } from "vuex-class";
import { TimePoint } from "../shared/chart/ChartUtils";
import { MultiChartConfig } from "../shared/chart/MultiChartWithSharedRange";

@Component({})
export default class HeartGraphs extends mixins(GraphRanges) {
  @Getter("graph", patientNamespace)
  graph!: any;

  @Getter("config", patientNamespace)
  config!: any;

  @Getter("current", patientNamespace)
  current!: Patient;

  @Getter("darkMode", navigationNamespace)
  darkMode!: boolean;

  @Prop(String)
  height!: string;

  get shockIndex() {
    const graphData = this.graph([{ key: "ShockIndex", label: "Shock Index" }]);
    Object.assign(graphData[0], {
      type: "line",
      fill: false,
      borderColor: "orange",
      backgroundColor: "orange",
      pointBackgroundColor: "orange",
      pointBorderColor: "orange",
      pointHoverBackgroundColor: "orange",
      pointHoverBorderColor: "orange"
    });

    const deliveryData = {
      label: "Delivery",
      data: []
    } as { label: string; data: TimePoint[] };

    const placentaData = {
      label: "Placenta",
      data: []
    } as { label: string; data: TimePoint[] };

    const ageDelivery = this.current.body.atoms.deliveryBaby;
    const agePlacenta = this.current.body.atoms.deliveryPlacenta;
    if (
      ageDelivery.age >= 0 &&
      (agePlacenta.age < 0 || ageDelivery.age - agePlacenta.age > 900)
    ) {
      deliveryData.data.push({ x: parseServerTime(ageDelivery.time), y: 2 });
    } else {
      placentaData.label = "Delivery and Placenta";
    }

    if (agePlacenta.age >= 0) {
      placentaData.data.push({ x: parseServerTime(agePlacenta.time), y: 2 });
    }
    Object.assign(deliveryData, {
      borderColor: "#000000",
      backgroundColor: "#000000"
    });

    Object.assign(placentaData, {
      borderColor: "#0088FF",
      backgroundColor: "#0088FF"
    });

    if (deliveryData.data.length) {
      graphData.push(deliveryData);
    }
    if (placentaData.data.length) {
      graphData.push(placentaData);
    }
    return graphData;
  }

  get styledGraph() {
    const diastolicColor = this.darkMode ? "#fff" : "#000";
    const heartRateColor = this.darkMode ? "#aec7e8" : "#0088ff";
    const colors = [
      heartRateColor,
      "#f80",
      "#00cc00",
      diastolicColor,
      "#cccccc"
    ];
    const styles = [
      "borderColor",
      "backgroundColor",
      "pointBackgroundColor",
      "pointBorderColor",
      "pointHoverBackgroundColor",
      "pointHoverBorderColor"
    ];

    const libraryDefaults = colors.map(color => {
      const librarySetting: Record<string, any> = {};
      styles.forEach(style => {
        librarySetting[style] = color;
      });
      librarySetting.showLines = true;
      librarySetting.fill = false;
      librarySetting.type = "line";
      librarySetting.lineTension = 0;

      return librarySetting;
    });

    const config: Record<string, any> = {};
    ["HeartRate", "BPSys", "BPMAP", "BPDias", "PulsePressure"].forEach(
      (key, i) => {
        config[key] = libraryDefaults[i];
      }
    );

    const userFriendlyNames: Record<string, string> = {
      BPSys: "Systolic",
      BPMAP: "MAP",
      BPDias: "Diastolic",
      HeartRate: "HeartRate",
      PulsePressure: "Pulse Pressure"
    };

    // Override HeartRate to be X's
    config.HeartRate = Object.assign(config.HeartRate, {
      showLine: false,
      pointStyle: "crossRot",
      pointRadius: 8,
      hoverRadius: 8,
      hoverBorderWidth: 3,
      borderWidth: 3
    });

    // Set pulse pressure to be bar chart
    config.PulsePressure = Object.assign(config.PulsePressure, {
      type: "bar"
    });

    const configKeys = Object.keys(config);

    const graphData = this.graph(configKeys, true);

    if (this.config.popups.Heart.display.pulsePressureOnGraph) {
      const pulsePressureData = {
        label: "PulsePressure",
        data: []
      } as any;
      const sys = _.find(graphData, { label: "BPSys" }) as any;
      const dias = _.find(graphData, { label: "BPDias" }) as any;
      if (sys && dias) {
        sys.data.forEach((sysPoint: any, index: number) => {
          if (dias.data[index] && sysPoint[0] === dias.data[index][0]) {
            pulsePressureData.data.push({
              x: sysPoint.x,
              y: sysPoint.y - dias.data[index].y
            });
          }
        });
        graphData.push(pulsePressureData);
      }
    }

    const output = graphData.map((dataSet: any) => {
      const label = dataSet.label;
      Object.keys(config[label]).forEach(key => {
        dataSet[key] = config[label][key];
      });

      if (userFriendlyNames[label]) {
        dataSet.label = userFriendlyNames[label];
      }
      return dataSet;
    });
    return output;
  }

  get styledPAPGraph() {
    const diastolicColor = this.darkMode ? "#fff" : "#000";
    const colors = ["#f80", "#00cc00", diastolicColor, "#cccccc"];
    const styles = [
      "borderColor",
      "backgroundColor",
      "pointBackgroundColor",
      "pointBorderColor",
      "pointHoverBackgroundColor",
      "pointHoverBorderColor"
    ];

    const libraryDefaults = colors.map(color => {
      const librarySetting: Record<string, any> = {};
      styles.forEach(style => {
        librarySetting[style] = color;
      });
      librarySetting.showLines = true;
      librarySetting.fill = false;
      librarySetting.type = "line";
      librarySetting.lineTension = 0;

      return librarySetting;
    });

    const config: Record<string, any> = {};
    ["PAPSys", "PAPDias", "CVP"].forEach((key, i) => {
      config[key] = libraryDefaults[i];
    });

    const userFriendlyNames: Record<string, string> = {
      PAPSys: "Systolic",
      PAPDias: "Diastolic",
      CVP: "CVP"
    };

    const configKeys = Object.keys(config);
    const graphData = this.graph(configKeys, true);

    const output = graphData.map((dataSet: any) => {
      const label = dataSet.label;
      Object.keys(config[label]).forEach(key => {
        dataSet[key] = config[label][key];
      });

      if (userFriendlyNames[label]) {
        dataSet.label = userFriendlyNames[label];
      }
      return dataSet;
    });
    return output;
  }

  get artCardiacIndexGraphData() {
    return this.graph([{ key: "ArtCardiacIndex", label: "Cardiac Index" }]);
  }

  get artCardiacOutputGraphData() {
    return this.graph([{ key: "ArtCardiacOutput", label: "Cardiac Output" }]);
  }

  get artStrokeVolumeIndexedGraphData() {
    return this.graph([
      { key: "ArtStrokeVolumeIndexed", label: "Stroke Volume Indexed" }
    ]);
  }

  get artStrokeVolumeGraphData() {
    return this.graph([{ key: "ArtStrokeVolume", label: "Stroke Volume" }]);
  }

  get artSystemicVascularResistanceIndexedGraphData() {
    return this.graph([
      {
        key: "ArtSystemicVascularResistanceIndexed",
        label: "Systemic Vascular Resistance Indexed"
      }
    ]);
  }

  get artSystemicVascularResistanceGraphData() {
    return this.graph([
      {
        key: "ArtSystemicVascularResistance",
        label: "Systemic Vascular Resistance"
      }
    ]);
  }

  shockIndexLibrary = {
    scales: {
      xAxes: [
        {
          maxBarThickness: 4,
          minBarThickness: 3,
          // Stacked so the bars don't move when one is hidden
          // they don't offset based on other data, more accuretly placed
          stacked: true
        }
      ]
    }
  };

  bpLibrary = {
    legend: {
      labels: {
        usePointStyle: true
      }
    },
    scales: {
      xAxes: [
        {
          barThickness: 5
        }
      ]
    }
  };

  papLibrary = {
    legend: {
      labels: {
        usePointStyle: true
      }
    },
    scales: {
      xAxes: [
        {
          barThickness: 5
        }
      ],
      yAxes: [
        {
          ticks: {
            beginAtZero: false,
            suggestedMin: -2
          }
        }
      ]
    }
  };

  get charts(): MultiChartConfig[] {
    const chartList: MultiChartConfig[] = [];
    if (this.config.popups.Heart.display.shockIndexGraph) {
      chartList.push({
        title: "Shock Index",
        data: this.shockIndex,
        library: this.shockIndexLibrary,
        suggestedMax: 2,
        type: "bar",
        maxHeight: "250px",
        flexBasis: "150px"
      });
    }
    chartList.push({
      title: "Blood Pressure (mmHg) and Heart Rate (Beats/Minute)*",
      data: this.styledGraph,
      library: this.bpLibrary,
      suggestedMax: 150,
      type: "bar"
    });

    if (this.graph("PAPDias", true).length) {
      const hasCVP = this.graph("CVP", true).length;
      const hasPAPSys = this.graph("PAPSys", true).length;
      const pulmCvpLabel =
        "Pulmonary Artery Pressure" +
        (hasPAPSys ? "s" : " Diastolic") +
        (hasCVP ? " & CVP" : "") +
        " (mmHg)*";

      chartList.push({
        title: pulmCvpLabel,
        data: this.styledPAPGraph,
        library: this.papLibrary,
        suggestedMax: 20,
        type: "bar"
      });
    } else {
      const estVolsGraph = this.graph(["CVP", "PPV", "SPV", "SVV"], true);
      const estVolsLabel = estVolsGraph
        .map((g: any) => g.label)
        .join(", ")
        .replace(/, ([^,]*)$/, " & $1");
      if (estVolsGraph.length) {
        chartList.push({
          title: estVolsLabel,
          data: estVolsGraph,
          library: {
            scales: {
              yAxes: [
                {
                  ticks: {
                    suggestedMax: 10,
                    suggestedMin: -2,
                    beginAtZero: false
                  }
                }
              ]
            }
          }
        });
      }
    }

    const artCardiacIndex = _.get(
      this,
      "current.body.atoms.artCardiacIndex.legend",
      ""
    );
    if (artCardiacIndex.includes("(CI)")) {
      if (
        this.artCardiacIndexGraphData.length &&
        this.artCardiacIndexGraphData[0].data.length > 0
      ) {
        chartList.push({
          title: "Cardiac Index (L/min/m<sup>2</sup>)",
          data: this.artCardiacIndexGraphData,
          min: 0,
          suggestedMax: 8
        });
      }
    } else if (artCardiacIndex.includes("(CO)")) {
      if (
        this.artCardiacOutputGraphData.length &&
        this.artCardiacOutputGraphData[0].data.length > 0
      ) {
        chartList.push({
          title: "Cardiac Output (L/min)",
          data: this.artCardiacOutputGraphData,
          min: 0,
          suggestedMax: 16
        });
      }
    }

    const artStrokeVolumeIndexed = _.get(
      this,
      "current.body.atoms.artStrokeVolumeIndexed.legend",
      ""
    );
    if (artStrokeVolumeIndexed.includes("(SVI)")) {
      if (
        this.artStrokeVolumeIndexedGraphData.length &&
        this.artStrokeVolumeIndexedGraphData[0].data.length > 0
      ) {
        chartList.push({
          title: "Stroke Volume Indexed (ml/beat/m<sup>2</sup>)",
          data: this.artStrokeVolumeIndexedGraphData,
          min: 0,
          suggestedMax: 80
        });
      }
    } else if (artStrokeVolumeIndexed.includes("(SV)")) {
      if (
        this.artStrokeVolumeGraphData.length &&
        this.artStrokeVolumeGraphData[0].data.length > 0
      ) {
        chartList.push({
          title: "Stroke Volume (ml/beat)",
          data: this.artStrokeVolumeGraphData,
          min: 0,
          suggestedMax: 160
        });
      }
    }

    const artSystemicVascularResistanceIndexed = _.get(
      this,
      "current.body.atoms.artSystemicVascularResistanceIndexed.legend",
      ""
    );
    if (artSystemicVascularResistanceIndexed.includes("(SVRI)")) {
      if (
        this.artSystemicVascularResistanceIndexedGraphData.length &&
        this.artSystemicVascularResistanceIndexedGraphData[0].data.length > 0
      ) {
        chartList.push({
          title:
            "Systemic Vascular Resistance Indexed (Dynes-sec/cm<sup>5</sup>/m<sup>2</sup>)",
          data: this.artSystemicVascularResistanceIndexedGraphData,
          min: 0,
          suggestedMax: 3000
        });
      }
    } else if (artSystemicVascularResistanceIndexed.includes("(SVR)")) {
      if (
        this.artSystemicVascularResistanceGraphData.length &&
        this.artSystemicVascularResistanceGraphData[0].data.length > 0
      ) {
        chartList.push({
          title: "Systemic Vascular Resistance (Dynes-sec/cm<sup>5</sup>)",
          data: this.artSystemicVascularResistanceGraphData,
          min: 0,
          suggestedMax: 6000
        });
      }
    }

    return chartList;
  }
}
